import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { todayInWorkspaceTz, type WorkspaceTask } from "@/hooks/useWorkspaceTasks";
import { TaskDatePicker } from "./TaskDatePicker";
import { TaskTimePicker } from "./TaskTimePicker";

interface TaskEditDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: WorkspaceTask | null;
  onSave: (input: {
    taskId: string;
    due_date: string | null;
    due_at: string | null;
    allowPastDue?: boolean;
  }) => Promise<void>;
}

function isOverdueTask(task: WorkspaceTask): boolean {
  if (!task.due_date || task.status !== "pending") return false;
  return task.due_date < todayInWorkspaceTz();
}

export function TaskEditDateDialog({ open, onOpenChange, task, onSave }: TaskEditDateDialogProps) {
  const [noDueDate, setNoDueDate] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const allowPastDue = useMemo(() => (task ? isOverdueTask(task) : false), [task]);

  useEffect(() => {
    if (!open || !task) return;
    const hasDate = Boolean(task.due_date);
    setNoDueDate(!hasDate);
    setDueDate(task.due_date ?? todayInWorkspaceTz());
    if (task.due_at) {
      const d = new Date(task.due_at);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      setDueTime(`${hh}:${mm}`);
    } else {
      setDueTime("");
    }
  }, [open, task]);

  const handleSubmit = async () => {
    if (!task) return;
    if (!noDueDate && !allowPastDue) {
      const today = todayInWorkspaceTz();
      if (dueDate < today) {
        toast({
          title: "Data inválida",
          description: "Não é possível definir data no passado.",
          variant: "destructive",
        });
        return;
      }
    }
    try {
      setSubmitting(true);
      const due_date = noDueDate ? null : dueDate;
      const due_at =
        !noDueDate && dueDate && dueTime
          ? new Date(`${dueDate}T${dueTime}:00`).toISOString()
          : null;
      await onSave({ taskId: task.id, due_date, due_at, allowPastDue });
      onOpenChange(false);
      toast({ title: "Data atualizada" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao atualizar data",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Alterar data</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground truncate">{task?.title}</p>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-no-due-date"
              checked={noDueDate}
              onCheckedChange={(v) => setNoDueDate(v === true)}
            />
            <Label htmlFor="edit-no-due-date" className="font-normal cursor-pointer">
              Sem data definida
            </Label>
          </div>
          {!noDueDate && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-task-date">Data</Label>
                <TaskDatePicker
                  id="edit-task-date"
                  value={dueDate}
                  onChange={setDueDate}
                  minDate={allowPastDue ? undefined : todayInWorkspaceTz()}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-task-time">Horário (opcional)</Label>
                <TaskTimePicker
                  id="edit-task-time"
                  value={dueTime}
                  onChange={setDueTime}
                  clearable
                />
              </div>
            </div>
          )}
          {allowPastDue && (
            <p className="text-xs text-muted-foreground">
              Tarefa atrasada: é permitido manter ou ajustar para uma data no passado.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
